import {
  EllipsisHorizontalIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  type DashboardActiveTransporter,
  fetchDispensaryActiveRunsApi,
  fetchOrgActiveRunsApi,
  mapActiveRunsToTransporters,
} from "../lib/dispensary-dashboard-api";
import { useDispensaryDriversLive } from "../hooks/useDispensaryDriversLive";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";
import { DispensaryMap } from "./DispensaryMap";

export type ActiveTransporter = DashboardActiveTransporter;

export function TransporterActivityPanel({ headerSearch = "" }: { headerSearch?: string }) {
  const { getToken, dispensaryId, orgId, allLocations } = useDispensaryWorkspace();
  const aggregate = allLocations && orgId != null;
  const { data: transporters = [], isLoading } = useQuery({
    queryKey: ["dispensary", "transporters", "active", aggregate ? `org:${orgId}` : dispensaryId],
    queryFn: async () => {
      const runs = aggregate
        ? await fetchOrgActiveRunsApi(getToken, orgId!)
        : await fetchDispensaryActiveRunsApi(getToken, dispensaryId!);
      return mapActiveRunsToTransporters(runs);
    },
    enabled: Boolean(dispensaryId) || aggregate,
    refetchInterval: 30_000,
  });
  // Live `{ driverId → { lat, lng } }` map, kept fresh by the dispensary
  // socket's `driver.location` subscription. Matches transporter-web's
  // `useTransporterDriversLive` pattern; supersedes the prior `lastLat`/
  // `lastLng` fields on the transporters list (which were hardcoded Denver
  // coordinates client-side).
  const positions = useDispensaryDriversLive(aggregate ? null : dispensaryId);

  const visibleTransporters = useMemo(() => {
    const q = headerSearch.trim().toLowerCase();
    if (!q) return transporters;
    return transporters.filter((t) => {
      const hay = [
        t.name,
        t.phone,
        t.driverId,
        t.vehicle.plate,
        t.vehicle.make,
        t.vehicle.model,
        String(t.vehicle.year),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [transporters, headerSearch]);

  return (
    <div className="flex w-[260px] flex-shrink-0 flex-col gap-4">
      <div className="overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        <div className="flex items-center justify-between border-b border-[#F0F0F0] px-4 py-4">
          <span className="text-sm font-bold text-[#111111]">Transporter Activity</span>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-lg transition-colors hover:bg-[#F5F5F3]"
            aria-label="More options"
          >
            <EllipsisHorizontalIcon className="h-4 w-4 text-[#AAAAAA]" />
          </button>
        </div>
        <div className="relative h-[200px]">
          {isLoading ? (
            <div className="absolute inset-0 animate-pulse bg-[#E8E8E4]" />
          ) : (
            <DispensaryMap transporters={visibleTransporters} positions={positions} />
          )}
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2.5 rounded-xl bg-white/95 px-3 py-2.5 shadow-[0_2px_8px_rgba(0,0,0,.10)] backdrop-blur-sm">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-green">
              <MapPinIcon className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="text-xs font-semibold text-[#111111]">Transporter Activity</span>
            <MapPinIcon className="ml-auto h-3.5 w-3.5 text-[#AAAAAA]" />
          </div>
        </div>
      </div>

    </div>
  );
}
